import { useEffect, useMemo, useState } from 'react'
import { Button, Empty, Spin, Tag , DatePicker, Switch, Typography} from 'antd'
import type { TimeRangePickerProps } from 'antd';
import { CloseOutlined, StarFilled } from '@ant-design/icons'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useFeedback } from '@/utils/antdApp'
import { getBacktestsHistory, getRecentBacktests , getBacktestsDeleted, deleteBacktest, archiveBacktest } from '@/api'
import type { BacktestDeatil } from '@/types'
import dayjs from 'dayjs';
import type { Dayjs } from 'dayjs';
import { localToUTCHMS ,filterMoneyPlus , filterFormatThousandNumber} from '@/utils/filters'
import './index.scss'

const { Text } = Typography

type BacktestTab = 'recent' | 'history' | 'deleted'
const { RangePicker } = DatePicker;

function BacktestCard({
    run,
    onClick,
    isCompareMode,
    checked,
    changeCheckBox,
    refresh,
    highlighted,
}: {
    run: BacktestDeatil
    onClick?: () => void | Promise<void>
    isCompareMode?: boolean
    checked?: boolean
    changeCheckBox?: (id: number, checked: boolean) => void
    refresh?: (isDeleted?: boolean) => void | Promise<void>
    highlighted?: boolean
}) {
    const returnClass =
        run.final_cumulative_return_pct != null && run.final_cumulative_return_pct < 0 ? 'negative' : 'positive'
    const drawdownClass = run.max_drawdown_pct != null && run.max_drawdown_pct < 0 ? 'negative' : 'positive'
    const { message, modal } = useFeedback()

    const onChange = (event: React.MouseEvent<HTMLSpanElement>, id: number, checked: boolean) => {
        event.stopPropagation()
        changeCheckBox && changeCheckBox(id, checked)
    }

    return (
        <article
            className={`backtests-page__card ${isCompareMode && checked ? 'is-compare' : ''} ${highlighted ? 'is-highlight' : ''}`}
            title={run.status === 2 ? run.results.error :''}
            onClick={onClick}
        >
            <div className={`backtests-page__card-header ${isCompareMode ? 'is-compare-header' : ''}`}>
                <div style={{flex: 1}}>
                    <div className="backtests-page__card-title font-weight-700">
                        {run.display_name || '默认策略'} 
                    </div>
                    <div className="backtests-page__card-subtitle">
                        {run.manager_name ?? '--'} · {run.strategy_tags? run.strategy_tags.map((tag) => tag).join(' | ') : '--'} | {run.config.freq ?? '--'} <span style={{marginLeft: 4, marginRight: 4}}>|</span> {run.backtest_range_start ?? '--'} - {run.backtest_range_end ?? '--'}
                    </div>
                </div>
                {
                    isCompareMode ? 
                    <span className={`backtests-page__radio-wrapper font-weight-700 ${checked ? 'is-checked' : ''}`} onClick={(event) => onChange(event, run.id, checked ? false : true)}>
                        {checked ? '✓':""}
                    </span>
                    :(
                        <div className='flex items-center gap-4'>
                            {
                                run.record_status === 1 && <StarFilled className='text-11' style={{color: '#f59e0b'}} />
                            }
                            {/* record_status: 1 已归档, 0 已完成, -1 已删除
                            status: 0 进行中, 1 已完成, 2 失败
                            */}
                            <Tag className={`label-tag text-10 
                                ${run.record_status === 1 ? 'is-tag-archived' : run.record_status === -1 || run.status === 2 ? 'is-error' : run.status === 1 ?'is-success':'is-default'}`} variant='outlined'>
                                {run.record_status === 1 ? '已归档' : run.record_status === -1 ? '已删除' : run.status === 1 ? '已完成' : run.status ===  2?'失败':'进行中'}
                            </Tag>
                        </div>
                    )
                }
            </div>

            <div className="backtests-page__card-tags">
                {
                    run.strategy_tags.map((tag) => (
                        <Tag key={tag} className='label-tag text-10'>{tag}</Tag>
                    ))
                }
            </div>

            <div className='backtests-page__metrics_box'>
                <div className="backtests-page__metrics">
                    <div className="backtests-page__metric">
                        <div className={`backtests-page__metric-value font-weight-700 ${returnClass}`}>
                            {filterMoneyPlus(run.final_cumulative_return_pct, 1)}%
                        </div>
                        <div className="backtests-page__metric-label">总收益</div>
                    </div>
                    <div className="backtests-page__metric">
                        <div className="backtests-page__metric-value">{filterFormatThousandNumber(run.sharpe??0)}</div>
                        <div className="backtests-page__metric-label">夏普比率</div>
                    </div>
                    <div className="backtests-page__metric">
                        <div className={`backtests-page__metric-value ${drawdownClass}`}>
                            {filterMoneyPlus(run.max_drawdown_pct, 1)}%
                        </div>
                        <div className="backtests-page__metric-label">最大回撤</div>
                    </div>
                    <div className="backtests-page__metric">
                        <div className="backtests-page__metric-value">{filterFormatThousandNumber(Number(run.win_rate??0), 2)}%</div>
                        <div className="backtests-page__metric-label">胜率</div>
                    </div>
                </div>
            </div>

            <div className="backtests-page__card-footer">
                <div className="backtests-page__footer-meta">
                    <span>
                        vs基准 
                        <b className={`${run.results.benchmarkReturn > 0 ? 'red' : run.results.benchmarkReturn < 0 ? 'green' : ''}`} style={{marginLeft: 7}}>
                            {filterMoneyPlus(run.results.benchmarkReturn, 1)}%
                        </b>
                    </span>
                </div>
                <div className="backtests-page__footer-actions flex flex-center gap-4">
                    <span className='text-11' style={{ color: 'var(--text-3)' }}>{localToUTCHMS(run.created_at) ?? '--'}</span>
                    {
                        run.record_status !== -1 && run.status === 1 && (
                            <span className={`backtests-page__operate-item flex flex-center ${run.record_status === 1?'is-archived':'is-default'}`} onClick={(e) => {
                                e?.stopPropagation()
                                archiveBacktest(run.id, run.record_status === 1 ? 0 : 1)
                                    .then(() => {
                                        message.success('操作成功')
                                        refresh && refresh()
                                    })
                                    .catch(() => {
                                        message.error('操作失败')
                                    })
                            }}>
                                <StarFilled className='text-11' style={{color: run.record_status === 1 ? '#f59e0b' : 'var(--text-3)'}} />
                            </span>
                        )
                    }
                    {
                        (run.record_status === 0 ) && 
                        <span className='backtests-page__operate-item flex flex-center' onClick={(e) => {
                            e?.stopPropagation()
                            modal.confirm({
                                title: '确定删除该回测吗？',
                                content: '删除后可在已删除列表中查看',
                                okText: '确定',
                                cancelText: '取消',
                                onOk: () => {
                                    deleteBacktest(run.id)
                                    .then(() => {
                                        message.success('删除成功')
                                        refresh && refresh(true)
                                    })
                                    .catch(() => {
                                        message.error('删除失败')
                                    })
                                }
                            })
                        }}>
                            <CloseOutlined className='text-11' style={{color: 'var(--text-3)'}} />
                        </span>
                    }
                    <Button
                        size="small"
                        className='text-10'
                        style={{ color: 'var(--text-2)' }}
                        onClick={(event) => {
                            event.stopPropagation()
                            if (isCompareMode) {
                                return
                            }
                            void onClick?.()
                        }}
                    >
                        查看详情
                    </Button>
                </div>
            </div>
        </article>
    )
}

export default function BacktestsPage() {
    const { message } = useFeedback()
    const navigate = useNavigate()
    const [searchParams, setSearchParams] = useSearchParams()
    const highlightRunId = (searchParams.get('highlight') || '').trim()
    const [activeTab, setActiveTab] = useState<BacktestTab>('recent')
    const [recentRuns, setRecentRuns] = useState<{
        before_this_week: BacktestDeatil[], 
        this_week: BacktestDeatil[],
        today: BacktestDeatil[]
    }>({
        before_this_week: [],
        this_week: [],
        today: [],
    })
    const recentRunsName = {
        today: '今天',
        this_week: '本周',
        before_this_week: '更早',
    }
    const [loading, setLoading] = useState(true)
    const [historyRuns, setHistoryRuns] = useState<BacktestDeatil[]>([])
    const [deletedRuns, setDeletedRuns] = useState<BacktestDeatil[]>([])
    const [isCompareMode, setIsCompareMode] = useState(false)
    const [compareRuns, setCompareRuns] = useState<number[]>([])
    const [isEmpty, setIsEmpty] = useState(false)

    const hasRunningRecent = useMemo(() => {
        const buckets = [recentRuns.today, recentRuns.this_week, recentRuns.before_this_week]
        return buckets.some((list) => list.some((run) => run.status === 0))
    }, [recentRuns])

    useEffect(() => {
        loadRecentBacktests()
        loadHistoryBacktests()
        loadDeletedBacktests()
    }, [])

    useEffect(() => {
        if (!highlightRunId) return
        const timer = window.setTimeout(() => {
            const next = new URLSearchParams(searchParams)
            next.delete('highlight')
            setSearchParams(next, { replace: true })
        }, 8000)
        return () => window.clearTimeout(timer)
    }, [highlightRunId, searchParams, setSearchParams])

    useEffect(() => {
        if (activeTab !== 'recent' || !hasRunningRecent) return
        const timer = window.setInterval(() => {
            loadRecentBacktests({ silent: true })
        }, 15000)
        return () => window.clearInterval(timer)
    }, [activeTab, hasRunningRecent])

    const loadRecentBacktests = (options?: { silent?: boolean }) => {
        if (!options?.silent) {
            setLoading(true)
        }
        getRecentBacktests()
            .then((data) => {
                setRecentRuns(data)
                setIsEmpty(
                    data.before_this_week.length === 0
                    && data.this_week.length === 0
                    && data.today.length === 0
                )
            })
            .finally(() => {
                if (!options?.silent) {
                    setLoading(false)
                }
            })
    }

    const loadHistoryBacktests = () => {
        getBacktestsHistory({start_date: dayjs().add(-30, 'd').format('YYYY-MM-DD'), end_date: dayjs().format('YYYY-MM-DD')})
            .then((data) => setHistoryRuns([...data]))
    }

    const loadDeletedBacktests = () => {
        getBacktestsDeleted()
            .then((data) => setDeletedRuns([...data]))
    }   

    const rangePresets: TimeRangePickerProps['presets'] = [
        { label: '最近一周', value: [dayjs().add(-7, 'd'), dayjs()] },
        { label: '最近两周', value: [dayjs().add(-14, 'd'), dayjs()] },
        { label: '最近30天', value: [dayjs().add(-30, 'd'), dayjs()] },
    ]

    const onRangeChange = (dates: null | (Dayjs | null)[], _dateStrings: string[]) => {
        if (dates) {
            getBacktestsHistory({start_date: dates[0]?.format('YYYY-MM-DD'), end_date: dates[1]?.format('YYYY-MM-DD')})
            .then((data) => setHistoryRuns([...data]))
        } else {
          console.log('Clear');
        }
    }

    const changeCheckBox = (id: number, checked: boolean) => {
        setCompareRuns((prev) => {
            if (checked) {
                if (prev.includes(id)) {
                    return prev
                }

                if (prev.length >= 5) {
                    message.error('最多选5个,至少选择2个可对比')
                    return prev
                }

                return [...prev, id]
            }

            return prev.filter((itemId) => itemId !== id)
        })
    }

    const refresh = (isDeleted?: boolean) => {
        //删除
        if (isDeleted) {
            loadDeletedBacktests()
        } 

        if (activeTab === 'history') {
            loadHistoryBacktests()
        } else if (activeTab === 'recent') {
            loadRecentBacktests()
        } 
    }

    return (
        <div className="backtests-page">
            <div className="page-header backtests-page__header">
                <div className="backtests-page__tabs">
                    <button
                        type="button"
                        className={`backtests-page__tab ${activeTab === 'recent' ? 'is-active' : ''}`}
                        onClick={() => setActiveTab('recent')}
                    >
                        最近回测
                    </button>
                    <button
                        type="button"
                        className={`backtests-page__tab ${activeTab === 'history' ? 'is-active' : ''}`}
                        onClick={() => setActiveTab('history')}
                    >
                        历史回测
                    </button>
                    <button
                        type="button"
                        className={`backtests-page__tab ${activeTab === 'deleted' ? 'is-active' : ''}`}
                        onClick={() => setActiveTab('deleted')}
                    >
                        已删除
                    </button>
                </div>

                <div className="backtests-page__actions">
                    <Button type="primary" onClick={() => navigate('/backtests/backtest')}>
                        <Text style={{ fontSize: 16 }}>+</Text> 新建回测
                    </Button>
                </div>
            </div>

            <div className="page-content backtests-page__content">
                {activeTab != 'deleted' && <div className="backtests-page__compare-mode flex items-center gap-4 text-12">
                        多选对比<Switch size="small" defaultChecked={isCompareMode}  onClick={() => {
                        setIsCompareMode(!isCompareMode)
                        setCompareRuns([])
                    }}/>
                </div>}
                {loading ? (
                        <div className="backtests-page__loading">
                            <Spin />
                        </div>
                    ) : activeTab === 'recent' ? (
                        isEmpty ? (
                                <div className="backtests-page__empty">
                                    <Empty description="暂无回测记录" />
                                </div>
                        ):(
                            Object.keys(recentRunsName).map((item) => {
                                return (
                                    recentRuns[item as keyof typeof recentRuns]?.length > 0 && (
                                        <section className="backtests-page__group" key={item}>
                                            <div className="backtests-page__group-title">{recentRunsName[item as keyof typeof recentRunsName]}</div>
                                            <div className="backtests-page__grid">
                                                {recentRuns[item as keyof typeof recentRuns].map((run) => (
                                                    <BacktestCard 
                                                    isCompareMode={isCompareMode} 
                                                    checked={compareRuns.includes(run.id)} 
                                                    changeCheckBox={changeCheckBox} 
                                                    key={run.id} 
                                                    run={run}
                                                    highlighted={Boolean(highlightRunId) && run.run_id === highlightRunId}
                                                    refresh={refresh}
                                                    onClick={() => {
                                                        if (isCompareMode) {
                                                            changeCheckBox(run.id, !compareRuns.includes(run.id))
                                                        } else {
                                                            if(run.status !== 0) {
                                                                navigate(`/backtests/backtest/${run.run_id}`)
                                                            }else{
                                                                message.warning('回测正在进行中，请稍后再试')
                                                            }
                                                        }
                                                    }}/>
                                                ))}
                                            </div>
                                        </section>
                                    ) 
                                )
                            })
                        )
                    ) : activeTab === 'history' ? (
                            <>
                                <section className="backtests-page__group">
                                    <div className="backtests-page__group-title">
                                        回测区间 
                                        <RangePicker 
                                            className='backtests-page__range-picker'
                                            style={{marginLeft: 10,marginRight: 10}} 
                                            placeholder={['开始日期', '结束日期']} 
                                            presets={rangePresets} 
                                            onChange={onRangeChange} 
                                            allowClear={false}
                                            disabledDate={(current, info) => {
                                                if (!info.from) return false
                                                return current.isBefore(info.from, 'day') || current.diff(info.from, 'day') > 30
                                            }}
                                        />
                                        <Button size="small" style={{color: 'var(--text-2)'}} className='text-12' onClick={() => navigate('/backtest')}>
                                            查询
                                        </Button>
                                        （最多选择30天）
                                    </div>
                                    {historyRuns.length > 0 ? (
                                        <div className="backtests-page__grid">
                                            {historyRuns.map((run) => (
                                                <BacktestCard 
                                                refresh={refresh}
                                                isCompareMode={isCompareMode} 
                                                checked={compareRuns.includes(run.id)} 
                                                changeCheckBox={changeCheckBox} 
                                                key={run.id} 
                                                run={run} 
                                                onClick={() => {
                                                    if (isCompareMode) {
                                                        return
                                                    } else {
                                                        navigate(`/backtests/backtest/${run.run_id}`)
                                                    }
                                                }}/>
                                            ))}
                                        </div>
                                    ) : (
                                        <div className="backtests-page__empty">
                                            <Empty description="暂无回测记录" />
                                        </div>
                                    )
                                }
                                </section>
                            </>
                        )
                        : (
                            deletedRuns.length > 0 ? (<div className="backtests-page__grid">
                                {deletedRuns.map((run) => (
                                    <BacktestCard isCompareMode={isCompareMode} key={run.id} run={run} onClick={() => {
                                        navigate(`/backtests/backtest/${run.run_id}`)
                                        }}/>
                                    ))}
                                </div>
                            ) : (
                                <div className="backtests-page__empty">
                                    <Empty description="暂无已删除回测记录" />
                                </div>
                            )
                        )
                    }
            </div>
            {
                isCompareMode && compareRuns.length && (
                    <div className="backtests-page__compare-modal">
                        <span style={{color: 'var(--accent)',fontWeight: 700}}>已选{compareRuns.length}个回测</span>
                        （最多选5个,至少选择2个可对比）
                        <Button
                            type="primary"
                            size="small"
                            className='text-12'
                            disabled={compareRuns.length < 2}
                            onClick={() => {
                                if (compareRuns.length < 2) {
                                    message.warning('至少选择2个回测进行对比')
                                    return
                                }
                                navigate(`/backtests/compare/${compareRuns.join(',')}`)
                            }}
                        >
                            对比所选({compareRuns.length})
                        </Button>
                        <Button size="small" style={{color: 'var(--text-2)'}} className='text-12' onClick={() => {
                            setIsCompareMode(false)
                        }}>
                            取消
                        </Button>
                    </div>
                )
            }
        </div>
    )
}
